// ORDER BY terms of a report grid query. No client or app imports here: the
// search API comparison harness (scripts/lead-grid-compare.ts) runs it in Node.
import type { ReportField } from '../../types/reporting.js';

export function fieldUiData(field: ReportField): Record<string, any> {
  return typeof field.ui_data === 'object' && field.ui_data !== null ? field.ui_data : {};
}

/**
 * The column a sort on `col` orders by. A field's ui_data `sort_by` names an
 * indexed key column for it (on large reports a sort on the displayed value
 * would read every row); otherwise a `<col>_label` sibling sorts by its label.
 * Sort-only key columns are never fetched (fieldNames).
 */
export function redirectSortColumn(col: string, fields: ReportField[]): string {
  const sortBy = fields.find((f) => f.name === col && typeof fieldUiData(f).sort_by === 'string');
  if (sortBy) return fieldUiData(sortBy).sort_by;
  const labelCol = `${col}_label`;
  if (fields.some((f) => f.name === labelCol)) return labelCol;
  return col;
}

/** The order rows come in when no sort is chosen: fields with ui_data `default_sort`. */
function defaultSortModel(fields: ReportField[]): Array<{ colId: string; sort: 'asc' | 'desc' }> {
  return fields
    .filter((f) => fieldUiData(f).default_sort === 'asc' || fieldUiData(f).default_sort === 'desc')
    .map((f) => ({ colId: f.name, sort: fieldUiData(f).default_sort }));
}

/**
 * The ORDER BY terms of a report query for this sort (the report's default
 * when none is chosen), ending with the id tie-breaker when the report has ids.
 */
export function reportOrderTerms(
  fields: ReportField[], sortModel: Array<{ colId: string; sort: 'asc' | 'desc' }>,
): Array<{ column: string; ascending: boolean }> {
  const effectiveSort = sortModel.length > 0 ? sortModel : defaultSortModel(fields);
  const terms = effectiveSort.map(({ colId, sort }) => ({ column: redirectSortColumn(colId, fields), ascending: sort === 'asc' }));
  // Stable pagination: server-side ranges over equal sort keys need a unique
  // tiebreaker or pages can repeat/skip rows (REG-648). It runs the same way
  // as the first sort so one (key, id) index serves each direction.
  if (terms.length > 0 && !terms.some((term) => term.column === 'id') && fields.some((field) => field.name === 'id')) {
    terms.push({ column: 'id', ascending: terms[0].ascending });
  }
  return terms;
}
